import {
  createContext,
  createElement,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from 'react';
import type { PropsWithChildren, ReactElement } from 'react';
import { GET_BASE_VIEWER_DATA, GET_HANGING_PROTOCOL_MATCH } from 'modules/Apollo/queries';
import useWorklistId from 'hooks/useWorklistId';
import { useRegisterStudyColors } from 'hooks/useStudyColor';
import type { Study, StudyGroup } from 'domains/viewer/ViewportsConfigurations/types';
import type {
  GetBaseViewerDataQuery,
  GetBaseViewerDataQueryVariables,
  GetHangingProtocolMatchQuery,
  SeriesClassificationSummary,
} from 'generated/graphql';
import { studyToSlimStudy } from '../../ViewportsConfigurations/manipulators';
import type { ApolloQueryResult } from '@apollo/client';
import { NetworkStatus } from '@apollo/client';
import { emitPrecacheStatus } from 'modules/Apollo/precache/emitPrecacheStatus';
import { usePrecacheQuery } from 'modules/Apollo/hooks/usePrecacheQuery';
import { useIsViewerMetadataPrecacheEnabled } from './useIsViewerMetadataPrecacheEnabled';
import { logRefetchDiff } from './logRefetchDiff';

import { deletePrecacheQuery } from 'modules/Apollo/precache/precacheOperation';
import { logger } from 'modules/logger';
import { FF, useFeatureFlagEnabled, useSplitFlag } from 'modules/feature-flags/suspense';
import { useActivePriorStudiesValue } from 'hooks/useActivePriorStudies';
import { useManualPriorStudies } from './useManualPriorStudies';
import { useHangingProtocolMatch } from './useHangingProtocolMatch';
import { GraphQLError } from 'graphql';
import { WithApolloContext } from 'modules/Apollo/types';
import { shouldCacheBaseViewerDataResult } from 'modules/Apollo/precache/baseViewerDataCachePolicy';
import type { ResultShouldCache } from 'modules/Apollo/links/precacheLink';

export const useStudyColorTracker = ({
  loadedStudies,
  pendingStudies,
}: {
  loadedStudies: string[];
  pendingStudies: string[];
}): void => {
  const registerStudyColors = useRegisterStudyColors();

  useEffect(() => {
    registerStudyColors(loadedStudies, pendingStudies);
  }, [loadedStudies, pendingStudies, registerStudyColors]);
};

export type BaseViewerDataHook = {
  studies: ReadonlyArray<Study>;
  mainStudiesIds: ReadonlyArray<string>;
  linkedStudiesIds: ReadonlyArray<string>;
  priorStudiesIds: ReadonlyArray<string>;
  autoloadedStudiesIds: ReadonlyArray<string>;
  loading: boolean;
  notFound: boolean;
  classifications: SeriesClassificationSummary[];
  smids?: Array<string>;
  refetch: () => Promise<ApolloQueryResult<GetBaseViewerDataQuery>>;
  hangingProtocol?: GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'];
  hangingProtocolLoading: boolean;
  /** Manually-added prior smids whose fetch terminally errored. */
  failedPriorSmids: ReadonlySet<string>;
  /** Clear a failed prior's state and re-attempt its fetch. */
  retryPriorStudy: (smid: string) => void;
  networkStatus: NetworkStatus;
  invalidatePrecacheForCurrentStudy: () => Promise<void>;
  invalidateAllPrecachedStudies: () => Promise<ApolloQueryResult<GetHangingProtocolMatchQuery>>;
};

export type UseBaseViewerDataProps = {
  shouldEmitPrecacheStatus?: boolean;
} & WithApolloContext;

export type BaseViewerDataContextValue = {
  result: BaseViewerDataHook;
  queryVariables: GetBaseViewerDataQueryVariables;
};

const BaseViewerDataContext = createContext<BaseViewerDataContextValue | undefined>(undefined);
BaseViewerDataContext.displayName = 'BaseViewerDataContext';

export const BaseViewerDataContextProvider = ({
  children,
  ...value
}: PropsWithChildren<BaseViewerDataContextValue>): ReactElement =>
  createElement(BaseViewerDataContext.Provider, { value }, children);

/**
 * Loads all base viewer data: the main case + autoloaded priors via GBVD,
 * manual priors via `useManualPriorStudies`, and the hanging-protocol match via
 * `useHangingProtocolMatch`.
 */
const useBaseViewerDataOwner = ({ context }: WithApolloContext): BaseViewerDataContextValue => {
  const worklistId = useWorklistId();
  const { priors: activePriorSmids, excluded: excludedAutoloadedPriorSmids } =
    useActivePriorStudiesValue(worklistId);
  const hasWorklistIds = worklistId != null;

  const shouldAutoloadPriors = useFeatureFlagEnabled(FF.AUTOLOAD_PRIORS);
  const ingestionTimeoutMs = useSplitFlag(FF.WORKLIST_INGESTION_TIMEOUT, Number);
  const resultShouldCache = useCallback<ResultShouldCache<GetBaseViewerDataQuery>>(
    (result) => shouldCacheBaseViewerDataResult({ result, ingestionTimeoutMs }),
    [ingestionTimeoutMs]
  );

  // GBVD's variables intentionally do NOT include `studyIds` anymore. Adding
  // or removing a manual prior must not change GBVD's variable identity, or
  // Apollo would re-normalize the entire (huge) GBVD response on every
  // click. Manual priors flow through `useManualPriorStudies` below; the
  // hanging-protocol match flows through `useHangingProtocolMatch`.
  const queryVariables = useMemo(
    () => ({
      worklistIds: [worklistId].filter((id): id is string => Boolean(id)),
      hasWorklistIds,
      includeAutoloadedPriors: shouldAutoloadPriors,
      skippedStudyIds: excludedAutoloadedPriorSmids as string[],
    }),
    [worklistId, hasWorklistIds, shouldAutoloadPriors, excludedAutoloadedPriorSmids]
  );

  const precacheEnabled = useIsViewerMetadataPrecacheEnabled();

  const { loading, data, previousData, refetch, error, networkStatus } = usePrecacheQuery<
    GetBaseViewerDataQuery,
    GetBaseViewerDataQueryVariables
  >(
    GET_BASE_VIEWER_DATA,
    {
      variables: queryVariables,
      skip: !hasWorklistIds,
      context: {
        ...context,
        resultShouldCache,
        telemetry: { ...context?.telemetry, domain: 'precache' },
      },
    },
    {
      precacheEnabled,
      onRefetch: logRefetchDiff,
    }
  );

  const {
    studies: manualPriorStudies,
    failedSmids: failedPriorSmids,
    retry: retryPriorStudy,
  } = useManualPriorStudies(activePriorSmids);

  const {
    hangingProtocol,
    loading: hangingProtocolLoading,
    refetch: refetchHangingProtocol,
  } = useHangingProtocolMatch({
    worklistId,
    studyIds: activePriorSmids,
    skippedStudyIds: excludedAutoloadedPriorSmids,
    includeAutoloadedPriors: shouldAutoloadPriors,
    context,
  });

  // We use the previous data to avoid flickering when the query is loading
  let latestPayload = data;

  if (latestPayload == null && previousData?.worklistItems != null) {
    const hasMatches = previousData?.worklistItems?.items.some(
      (worklist) => worklist.smid === worklistId
    );

    if (hasMatches) {
      latestPayload = previousData;
    }
  }

  const mainStudiesIds = useMemo(
    () =>
      latestPayload?.worklistItems?.items
        .flatMap((worklist) => [...worklist.studies, ...(worklist.linkedStudies ?? [])])
        .map((study) => study.smid) ?? [],
    [latestPayload]
  );

  // Studies from linked/grouped cases (as opposed to the worklist item's own studies) - these are
  // co-primary rather than priors, but still count toward the Viewer's total loaded-study cap.
  const linkedStudiesIds = useMemo(
    () =>
      latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.linkedStudies ?? [])
        .map((study) => study.smid) ?? [],
    [latestPayload]
  );

  const priorStudiesIds = useMemo(() => {
    const mainSet = new Set(mainStudiesIds);
    return [
      ...(latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.autoloadedPriors ?? [])
        .map((study) => study.smid) ?? []),
      ...manualPriorStudies.map((study) => study.smid),
    ].filter((smid) => !mainSet.has(smid));
  }, [latestPayload, manualPriorStudies, mainStudiesIds]);

  // lists the autoloaded priors available in the case, even if they've been skipped
  // this exists as a stable, reliable way to handle re-adding priors that were skipped
  // without having to check the url to see if that prior's smid is in the skipped params
  const autoloadedStudiesIds = useMemo(
    () => [
      ...(latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.allAutoloadedPriors ?? [])
        .map((study) => study.smid) ?? []),
    ],
    [latestPayload]
  );

  const studies = useMemo(() => {
    const mainStudies =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.studies) ?? [];

    const linkedStudies =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.linkedStudies ?? []) ?? [];

    const autoloadedPriors =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.autoloadedPriors) ?? [];

    // mainStudies + linkedStudies take priority: priors that share a SMID are dropped
    const seenSmids = new Set<string>();
    return [...mainStudies, ...linkedStudies, ...autoloadedPriors, ...manualPriorStudies]
      .filter((study): study is Study => Boolean(study))
      .filter((study) => {
        if (seenSmids.has(study.smid)) return false;
        seenSmids.add(study.smid);
        return true;
      });
  }, [latestPayload, manualPriorStudies]);

  const smids = useMemo(() => studies.map((study) => study.smid), [studies]);

  const invalidatePrecacheForCurrentStudy = useCallback(async () => {
    logger.info('Deleting precache query GET_BASE_VIEWER_DATA with variables:', queryVariables);
    await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA, variables: queryVariables });
  }, [queryVariables]);

  // This is a failsafe mechanism to delete the cache if the query returns no studies
  // If no studies are returned it's VERY likely something in our cache is wrong and
  // it does no harm to delete it for this query since it's theoretically empty anyway
  useEffect(() => {
    if (!loading && studies.length === 0 && worklistId != null) {
      invalidatePrecacheForCurrentStudy();
    }
  }, [loading, studies.length, worklistId, invalidatePrecacheForCurrentStudy]);

  const unableToFindStudyError =
    // @ts-expect-error [EN-10263] - TS2345 - Argument of type '(err: GraphQLError) => boolean' is not assignable to parameter of type '(value: GraphQLFormattedError, index: number, array: readonly GraphQLFormattedError[]) => unknown'.
    error?.graphQLErrors?.some((err: GraphQLError) => err.extensions.code === 404) ?? false;

  const notFound = !loading && (unableToFindStudyError || studies.length === 0);

  // Cached HPs can become stale when HPs are created/updated/deleted, so we
  // want to invalidate any HP-affecting state when that happens. The HP
  // match now lives in its own query, so refetching it directly is the
  // right primitive (GBVD itself no longer holds the HP match).
  const invalidateAllPrecachedStudies = useCallback(async () => {
    await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });
    // The HP match keeps its own precache entry, and it has to go too.
    //
    // `useHangingProtocolMatch` runs a plain `useQuery` and sets no `precachePolicy`, so it
    // inherits `precacheLink`'s default of `readonly-cache-first`: when an entry exists the link
    // answers from it and never reaches the network. Apollo's `refetch()` bypasses the
    // InMemoryCache but not that link, so clearing only GBVD left the refetch below being served
    // the pre-mutation protocol. Every caller of this function is reacting to an HP create,
    // update or delete, which is exactly when that entry is guaranteed wrong.
    //
    // Measured on a cross-window step delete: the server held 4 steps and the refetch returned 6.
    await deletePrecacheQuery({ query: GET_HANGING_PROTOCOL_MATCH });
    // Return the HP-match refetch so callers can read the post-invalidation
    // match (now its own query) without a second round-trip.
    return refetchHangingProtocol();
  }, [refetchHangingProtocol]);

  const classifications = useMemo(() => {
    const mainStudies =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.studies) ?? [];

    const linkedStudies =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.linkedStudies ?? []) ?? [];

    const autoloadedPriorStudies =
      latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.autoloadedPriors)
        .filter(Boolean) ?? [];

    const series = [
      ...mainStudies,
      ...linkedStudies,
      ...autoloadedPriorStudies,
      ...manualPriorStudies,
    ].flatMap((study) => study?.seriesList);

    return series
      .map((series) => (series?.classifications != null ? series.classifications[0] : null))
      .filter(
        (classification): classification is SeriesClassificationSummary => classification != null
      );
  }, [latestPayload, manualPriorStudies]);

  return {
    queryVariables,
    result: {
      smids,
      studies,
      mainStudiesIds,
      linkedStudiesIds,
      priorStudiesIds,
      autoloadedStudiesIds,
      loading,
      notFound,
      refetch,
      hangingProtocol,
      hangingProtocolLoading,
      failedPriorSmids,
      retryPriorStudy,
      classifications,
      networkStatus,
      invalidatePrecacheForCurrentStudy,
      invalidateAllPrecachedStudies,
    },
  };
};

export const BaseViewerDataProvider = ({
  children,
  context,
}: PropsWithChildren<WithApolloContext>): ReactElement => {
  const value = useBaseViewerDataOwner({ context });

  return createElement(BaseViewerDataContextProvider, { ...value }, children);
};

/**
 * Reads the single app-owned base-viewer-data result. Consumer telemetry contexts are retained in
 * the signature for compatibility; the provider owns the query context and Apollo observables.
 */
export const useBaseViewerData = ({
  shouldEmitPrecacheStatus = false,
}: UseBaseViewerDataProps): BaseViewerDataHook => {
  const value = useContext(BaseViewerDataContext);
  if (value == null) {
    throw new Error('useBaseViewerData must be read inside BaseViewerDataProvider');
  }

  const emittedWorklistId = useRef<string | undefined>(undefined);
  const worklistId = value.queryVariables.worklistIds[0];

  useEffect(() => {
    if (
      !shouldEmitPrecacheStatus ||
      worklistId == null ||
      emittedWorklistId.current === worklistId
    ) {
      return;
    }

    emitPrecacheStatus(GET_BASE_VIEWER_DATA, value.queryVariables);
    emittedWorklistId.current = worklistId;
  }, [shouldEmitPrecacheStatus, value.queryVariables, worklistId]);

  return value.result;
};

export type StudiesHookResult = {
  groups: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
} & BaseViewerDataHook;
export const useStudies = ({ context }: WithApolloContext): StudiesHookResult => {
  const {
    smids = [],
    studies,
    mainStudiesIds,
    priorStudiesIds,
    autoloadedStudiesIds,
    ...studiesHook
  } = useBaseViewerData({ context });
  const { groups, groupedStudies } = useMemo(
    () => groupStudies({ smids, studies, mainStudiesIds }),
    [smids, studies, mainStudiesIds]
  );

  return {
    groups,
    groupedStudies,
    studies,
    mainStudiesIds,
    priorStudiesIds,
    autoloadedStudiesIds,
    ...studiesHook,
  };
};

export function groupStudies({
  smids,
  studies,
  mainStudiesIds,
}: {
  smids: readonly string[];
  studies: readonly Study[];
  mainStudiesIds: readonly string[];
}): {
  groups: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
} {
  const currentCaseStudies = studies.filter((study) => mainStudiesIds.includes(study.smid));
  const primaryStudySmids = currentCaseStudies.map((study) => study.smid);

  const groups = [
    primaryStudySmids,
    ...smids.filter((smid) => !primaryStudySmids.includes(smid)).map((smid) => [smid]),
  ].filter((group) => group.length > 0);

  const groupedStudies = groups.map((group) =>
    group.reduce<StudyGroup>((acc, studySmid) => {
      const study = studies.find((study) => study.smid === studySmid);
      if (study == null) return acc;
      return [...acc, studyToSlimStudy(study)];
    }, [])
  );

  return { groups, groupedStudies };
}
